import { Page, QueryBase } from "../../../shared/models/models";
import { BudgetTypeCode, PaymentTermsCode, PriorityTypeCode, ServiceRequestStatusCode, ServiceTypeCode } from "../../servicing/models/models";

export enum ServiceBoardSortParameter {
  openDate = 1,
  createdDate = 2,
}

export type ServiceBoardFilterType = "newToday" | "expiringSoon";

export interface ServiceBoardQuery extends QueryBase<ServiceBoardSortParameter> {
  starredOnly?: boolean;
  urgentOnly?: boolean;
  dateFrom?: string;
  dateTo?: string;
  machineBrandIds?: string[];
  newTodayOnly?: boolean;
  expiringSoonOnly?: boolean;
  withMyOffersOnly?: boolean;
  // Advanced filters (matching map view)
  priorityTypeCodes?: number[];
  serviceTypeCodes?: number[];
  countryIds?: number[];
}

export interface PublicServiceRequestMachine {
  id: string;
  name: string;
  model: string;
  serialNumber: string;
  internalNumber?: string;
  productionYear?: number;
  machineCategoryName?: string;
  brandName: string;
  brandId?: string;
  systemBrandName?: string;
  system: string;
  imageUrl?: string;
  catalogImageUrl?: string;
}

export interface PublicServiceRequestCustomer {
  id: string;
  name: string;
  logoUrl?: string;
}

export interface PublicServiceRequestFile {
  name: string;
  content: string;
  size?: number;
  createdDate: string;
}

export interface PublicServiceRequest {
  id: string;
  internalNumber: string;
  issue: string;
  description: string;
  affectedPartCategoryId?: string;
  openDate?: string;
  statusCode: ServiceRequestStatusCode;
  serviceTypeCode: ServiceTypeCode;
  priorityTypeCode: PriorityTypeCode;
  flexibleDates: boolean;
  serviceStartDate?: string;
  serviceEndDate?: string;
  worksiteAccessEnabled: boolean;
  workingHoursFrom?: string;
  workingHoursTo?: string;
  weekendWorkPossible?: boolean;
  afterHourAccessAvailable?: boolean;
  budgetEnabled: boolean;
  budgetTypeCode?: BudgetTypeCode;
  fixedBudget?: number;
  budgetRangeFrom?: number;
  budgetRangeTo?: number;
  paymentTermsEnabled: boolean;
  paymentTermsCode?: PaymentTermsCode;
  notes?: string;
  alarmCode?: string;
  locationId?: string;
  locationName?: string;
  parentLocationName?: string;
  machine: PublicServiceRequestMachine;
  customer: PublicServiceRequestCustomer;
  serviceReportNumber?: string;
  files?: PublicServiceRequestFile[];
  offersCount: number;
  isOwnCompany: boolean;
  visibilityCode?: number;
  targetPartnerId?: string;
  targetPartnerName?: string;
  isDirectToMe?: boolean;
  hasMyOffer?: boolean;
  createdDate: string;
  modifiedDate: string;
}

export interface MachineBrandFilter {
  id: string;
  name: string;
  count: number;
}

export interface CountryFilter {
  id: number;
  name: string;
  count: number;
}

export interface ServiceBoardFilters {
  machineBrands: MachineBrandFilter[];
  countries?: CountryFilter[];
}

export interface ServiceBoardStats {
  newToday: number;
  expiringSoon: number;
  myOffers: number;
}

export interface ServiceBoardState {
  query: ServiceBoardQuery;
  serviceRequests?: Page<PublicServiceRequest>;
  serviceRequest?: PublicServiceRequest;
  filters?: ServiceBoardFilters;
  stats?: ServiceBoardStats;
  loading: boolean;
  success?: string;
  error: any;
  // Map view state
  mapData?: MapViewData;
  mapLoading: boolean;
  mapError: any;
  selectedCity: string | null;
  // Service provider settings
  providerSettings?: ServiceProviderSettings;
  providerSettingsLoading: boolean;
}

export interface StarredItem {
  id: string;
  internalNumber: string;
}

// ═══════════════════════════════════════════════════════════════
//  MAP VIEW TYPES
// ═══════════════════════════════════════════════════════════════

export interface MapViewQuery {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  priorityTypeCodes?: number[];
  serviceTypeCodes?: number[];
  machineBrandIds?: string[];
  countryIds?: number[];
  newTodayOnly?: boolean;
  expiringSoonOnly?: boolean;
  ownerCustomerId?: string;
}

export interface MapViewData {
  locations: MapLocation[];
  summary: MapSummary;
}

export interface MapLocation {
  city: string;
  countryName: string;
  countryId: number;
  latitude: number;
  longitude: number;
  totalCount: number;
  urgentCount: number;
  highCount: number;
  lowCount: number;
  requests: MapServiceRequest[];
}

export interface MapServiceRequest {
  id: string;
  internalNumber: string;
  issue: string;
  createdDate: string;
  openDate?: string;
  priorityTypeCode: number;
  serviceTypeCode: number;
  budgetTypeCode?: number;
  fixedBudget?: number;
  budgetRangeFrom?: number;
  budgetRangeTo?: number;
  flexibleDates: boolean;
  serviceStartDate?: string;
  serviceEndDate?: string;
  offersCount: number;
  isOwnCompany: boolean;
  visibilityCode?: number;
  targetPartnerId?: string;
  isDirectToMe?: boolean;
  hasMyOffer?: boolean;
  customer: {
    id: string;
    name: string;
    logoUrl?: string;
    latitude?: number | null;
    longitude?: number | null;
    city?: string;
  };
  machine: {
    name: string;
    brandName: string;
    model: string;
  };
}

export interface MapSummary {
  totalLocations: number;
  totalRequests: number;
  urgentRequests: number;
}

// ═══════════════════════════════════════════════════════════════
//  SERVICE PROVIDER SETTINGS TYPES
// ═══════════════════════════════════════════════════════════════

export interface ServiceProviderSettings {
  serviceRadiusKm?: number | null;
  baseLatitude?: number | null;
  baseLongitude?: number | null;
  effectiveLatitude?: number | null;
  effectiveLongitude?: number | null;
}

export interface ChangeServiceProviderSettings {
  serviceRadiusKm?: number | null;
  baseLatitude?: number | null;
  baseLongitude?: number | null;
}
